import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "..";
import { addSnack } from "../store/slices/ui";
import { jwtDecode } from "jwt-decode";
import { setLoginLogic } from "../store/slices/login";
import { refreshToken } from "./crosspay-service";
import { setupApiProgressInterceptor } from "../utils/api-progress-interceptor";

function getAuthToken(): string | null {
  if (store.getState().login.loggedUser === null) return null;
  const token = store.getState().login.loggedUser!.token;
  return token;
}

function getLocal(): string {
  return store.getState().ui.locale;
}

function getRefreshToken(): string | null {
  if (store.getState().login.loggedUser === null) return null;
  const token = store.getState().login.loggedUser!.refreshToken;
  return token;
}

class HttpService {
  private client: AxiosInstance;

  constructor(baseURL: any) {
    this.client = axios.create({
      baseURL: baseURL,
      timeout: 100000,
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.configureInterceptors();
    // Setup API progress tracking
    setupApiProgressInterceptor(this.client, () => store.dispatch);
  }

  private configureInterceptors() {
    this.client.interceptors.request.use(
      async (config) => {
        var token = getAuthToken();
        if (token === null) return config;
        if (config.url && !config.url.includes("/RefreshToken")) {
          var data = jwtDecode(token);
          if (data && data.exp) {
            const expiration = data.exp * 1000; // Convert milliseconds from seconds
            const currentTime = new Date().getTime();
            if (currentTime > expiration) {
              var rToken = getRefreshToken();
              if (rToken != null) {
                var response = await refreshToken(rToken);
                store.dispatch(setLoginLogic(response));
                if (response) {
                  token = response && response?.result.token;
                }
              }
            } else {
              console.log("Token is valid");
            }
          }
        }

        config.headers.Authorization = `Bearer ${token}`;

        config.headers["Accept-Language"] = getLocal();

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, {
        ...config,
        headers: {
          ...config?.headers,
        },
      });

      return response ? response.data : null;
    } catch (error: any) {
      var response = {};
      var errorMessage = error.message;
      if (error && error.response && error.response.data) {
        response = error.response.data;
        errorMessage = error.response.data.errorMessage ?? errorMessage;
      }
      store.dispatch(addSnack({ message: errorMessage, color: "#B23B3B" }));
      return Promise.resolve(response as T);
    }
  }
  async fileDownload<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    try {
      const response: AxiosResponse<Blob> = await this.client.get(url, {
        ...config,
        responseType: "arraybuffer",
      });

      const contentDisposition = response.headers["content-disposition"];
      const contentType = response.headers["content-type"];

      let extension = "bin";

      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match?.[1]) {
          const fileName = match[1].replace(/['"]/g, "");
          extension = fileName.split(".").pop() || extension;
        }
      }

      if (!contentDisposition || extension === "bin") {
        const mimeTypeToExtension: Record<string, string> = {
          "application/pdf": "pdf",
          "image/jpeg": "jpg",
          "image/png": "png",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            "xlsx",
          "application/zip": "zip",
        };

        extension =
          mimeTypeToExtension[contentType?.toLowerCase() || ""] || "bin";
      }
      const pdfBlob = new Blob([response.data], {
        type: response.headers["Content-Type"]?.toString() ?? "",
      });

      // Create a temporary URL for the Blob
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const date = today.getDate();
      var fileName = `${month}/${date}/${year}`;

      // Create a temporary <a> element to trigger the download
      const tempLink = document.createElement("a");
      tempLink.href = downloadUrl;
      tempLink.setAttribute("download", `${fileName}.${extension}`); // Set the desired filename for the downloaded file

      // Append the <a> element to the body and click it to trigger the download
      document.body.appendChild(tempLink);
      tempLink.click();

      // Clean up the temporary elements and URL
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(downloadUrl);
      return null;
    } catch (error: any) {
      var response = {};
      var errorMessage = error.message;
      if (error && error.response && error.response.data) {
        response = error.response.data;
        errorMessage = error.response.data.errorMessage ?? errorMessage;
      }
      store.dispatch(addSnack({ message: errorMessage, color: "#B23B3B" }));
      return Promise.resolve(response as T);
    }
  }
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, config);
      return response ? response.data : null;
    } catch (error: any) {
      var response = {};
      var errorMessage = error.message;
      if (error && error.response && error.response.data) {
        response = error.response.data;
        errorMessage = error.response.data.errorMessage ?? errorMessage;
      }
      store.dispatch(addSnack({ message: errorMessage, color: "#B23B3B" }));
      return Promise.resolve(response as T);
    }
  }
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.client.post(
        url,
        data instanceof FormData ? data : JSON.stringify(data),
        {
          ...config,
          headers:
            data instanceof FormData
              ? {
                  ...config?.headers,
                  "Content-Type": "multipart/form-data",
                }
              : config?.headers, // Maintain existing headers for plain objects
        },
      );
      return response ? response.data : null;
    } catch (error: any) {
      var response = {};
      var errorMessage = error.message;
      if (error && error.response && error.response.data) {
        response = error.response.data;
        var fallbackErrorMessage = error.response?.data?.message;
        errorMessage =
          error.response.data.errorMessage ??
          fallbackErrorMessage ??
          errorMessage;
      }
      store.dispatch(addSnack({ message: errorMessage, color: "#B23B3B" }));
      return Promise.resolve(response as T);
    }
  }
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.client.put(
        url,
        data instanceof FormData ? data : JSON.stringify(data),
        {
          ...config,
          headers:
            data instanceof FormData
              ? {
                  ...config?.headers,
                  "Content-Type": "multipart/form-data",
                }
              : config?.headers,
        },
      );
      return response ? response.data : null;
    } catch (error: any) {
      var response = {};
      var errorMessage = error.message;
      if (error && error.response && error.response.data) {
        response = error.response.data;
        errorMessage = error.response.data.errorMessage ?? errorMessage;
      }
      store.dispatch(addSnack({ message: errorMessage, color: "#B23B3B" }));
      return Promise.resolve(response as T);
    }
  }
}

const HttpFileManagment = new HttpService(
  process.env.REACT_APP_FILES_MANAGMENT_BASE_URL,
);
const HttpCrossPay = new HttpService(process.env.REACT_APP_CROSSPAY_BASE_URL);
const HttpExternalService = (url: string) => new HttpService(url);
export { HttpCrossPay, HttpFileManagment, HttpExternalService };
